import { sum } from '../../../../../helpers/array'
import type {
  OwnedEquipment,
  LeasedEquipment,
  EquipmentList,
  EquipmentListWithStats,
  EquipmentStatistics
} from '../types'

type StandardItemStatistics = {
  annualCostToOwn: number,
  totalAnnualCost: number,
  hourlyCostToRun: number
}

type OwnedEquipmentWithStats = OwnedEquipment & StandardItemStatistics
type LeasedEquipmentWithStats = LeasedEquipment & StandardItemStatistics

const calculateOwnedItemStatistics = (equipment: OwnedEquipment): OwnedEquipmentWithStats => {
  const annualCostToOwn = equipment.yearsUsed === 0
    ? 0
    : (equipment.purchasePrice - equipment.sellPrice) / equipment.yearsUsed
  const hourlyCostToRun = equipment.billableHoursPerYear === 0
    ? 0
    : annualCostToOwn / equipment.billableHoursPerYear
  return {
    ...equipment,
    annualCostToOwn,
    totalAnnualCost: annualCostToOwn * equipment.quantity,
    hourlyCostToRun
  }
}

const calculateLeasedItemStatistics = (equipment: LeasedEquipment): LeasedEquipmentWithStats => {
  const annualCostToOwn = equipment.monthlyPayment * 12
  const hourlyCostToRun = equipment.billableHoursPerYear === 0
    ? 0
    : annualCostToOwn / equipment.billableHoursPerYear
  return {
    ...equipment,
    annualCostToOwn,
    totalAnnualCost: annualCostToOwn * equipment.quantity,
    hourlyCostToRun
  }
}

export const calculateItemStatistics = (equipmentList: EquipmentList): EquipmentListWithStats => {
  return {
    owned: equipmentList.owned.map(calculateOwnedItemStatistics),
    leased: equipmentList.leased.map(calculateLeasedItemStatistics),
    misc: equipmentList.misc
  }
}

export const calculateOverallStatistics = (equipmentList: EquipmentListWithStats): EquipmentStatistics  => {
  const overheadAnnualCost = sum([
    ...equipmentList.owned.filter(x => x.recoveryStrategy === 'OVERHEAD'),
    ...equipmentList.leased.filter(x => x.recoveryStrategy === 'OVERHEAD'),
    ...equipmentList.misc
  ], x => x.totalAnnualCost)
  const billableAnnualCost = sum([
    ...equipmentList.owned.filter(x => x.recoveryStrategy === 'BILLABLE'),
    ...equipmentList.leased.filter(x => x.recoveryStrategy === 'BILLABLE')
  ], x => x.totalAnnualCost)
  return {
    overheadAnnualCost,
    billableAnnualCost,
    totalAnnualCost: overheadAnnualCost + billableAnnualCost
  }
}
